import React, { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { axiosInstance } from '../context/AuthContext'
import Navbar from '../components/Navbar'
import { useAuthContext } from '../hooks/useAuthContext'

const ViewBlogPage = () => {
  const { id } = useParams()
  const navigate = useNavigate()
  const { user } = useAuthContext()
  const [blog, setBlog] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    fetchBlog()
  }, [id])

  const fetchBlog = async () => {
    try {
      setIsLoading(true)
      setError('')
      const response = await axiosInstance.get(`/blog/${id}`)
      setBlog(response.data)
    } catch (error) {
      console.error('Error fetching blog:', error)
      setError(error.response?.data?.error || 'Blog not found')
    } finally {
      setIsLoading(false)
    }
  }

  if (isLoading) {
    return (
      <div className="flex">
        {user && <Navbar />}
        <main className={`flex-1 ${user ? 'sm:ml-20 ml-0' : ''} p-6 max-w-4xl mx-auto`}>
          <div className="text-center py-12 pt-6">
            <div className="text-lg text-gray-600">Loading blog post...</div>
          </div>
        </main>
      </div>
    )
  }

  if (error || !blog) {
    return (
      <div className="flex">
        {user && <Navbar />}
        <main className={`flex-1 ${user ? 'sm:ml-20 ml-0' : ''} p-6 max-w-4xl mx-auto`}>
          <div className="text-center py-12">
            <h1 className="text-2xl font-semibold mb-4 text-gray-900">Blog Not Found</h1>
            <p className="text-gray-600 mb-6">{error || 'The blog post you are looking for does not exist.'}</p>
            <button
              onClick={() => navigate('/')}
              className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
            >
              Go Home
            </button>
          </div>
        </main>
      </div>
    )
  }

  return (
    <div className={`flex ${user ? 'sm:ml-20 ml-0' : ''}`}>
      {user && <Navbar />}
      <main className={`flex-1 p-6 max-w-4xl mx-auto p`}>
        <article className="bg-white pt-8">
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-8">
            {blog.title}
          </h1>

          {blog.featuredImage ? (
            <div className="w-full mb-8 rounded-2xl overflow-hidden">
              <img
                src={blog.featuredImage}
                alt={blog.title}
                className="w-full aspect-[4/3] object-cover"
              />
            </div>
          ) : (
            <div className="w-full h-96 mb-8 rounded-2xl bg-gray-100 flex items-center justify-center">
              <p className="text-gray-400">No image available</p>
            </div>
          )}

          <div className="mb-8">
            {blog.excerpt && (
              <div className="mb-6">
                <p className="text-lg text-gray-700 leading-relaxed">{blog.excerpt}</p>
              </div>
            )}

            <div
              className="prose prose-lg max-w-none blog-content text-gray-700 leading-relaxed"
              dangerouslySetInnerHTML={{ __html: blog.content }}
            />
          </div>

          <div className="flex flex-wrap items-center gap-4 text-sm text-gray-600 pt-6 border-t border-gray-200">
            {blog.author && (
              <div className="flex items-center gap-2">
                {blog.author.avatar || blog.author.photo ? (
                  <img
                    src={blog.author.avatar || blog.author.photo}
                    alt={blog.authorName || 'Author'}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-gray-600 text-xs">
                      {(blog.authorName || blog.author?.email || 'A')[0].toUpperCase()}
                    </span>
                  </div>
                )}
                <span className="font-medium">
                  {blog.authorName || `${blog.author?.firstName || ''} ${blog.author?.lastName || ''}`.trim() || blog.author?.email || 'Unknown Author'}
                </span>
              </div>
            )}
            <span>•</span>
            <time dateTime={blog.createdAt}>
              {new Date(blog.createdAt).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric',
              })}
            </time>
          </div>
        </article>

        <div className="mt-6">
          <button
            onClick={() => navigate(-1)}
            className="px-4 py-2 text-gray-700 hover:text-gray-900 transition flex items-center gap-2"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M15 19l-7-7 7-7"
              />
            </svg>
            Back
          </button>
        </div>
      </main>
    </div>
  )
}

export default ViewBlogPage

